import {DefectSignature} from '../types';
import {
    classifyDefect,
    defectKindDisplay,
} from './classifyDefect';

// FNP-904 — turn a signature into a ticket a human can paste into Jira.
//
// ═══ WHY THIS IS A DRAFT AND NOT AN API CALL ═══
//
// The ticket asks for one-click filing that creates the FNP issue directly.
// There is no Jira integration anywhere on this platform and a browser cannot
// hold an API token, so unattended creation would need a new outbound
// integration, a secret, and an auth surface — a decision well outside a console
// module. Owner's call (2026-08-14): ship the drawer with a generated body and a
// place to paste the key back, which is real end-to-end today and becomes the
// body-generator for the automated path when it lands. The `jiraKey` half is
// fully wired either way.
//
// The generated body deliberately carries the EVIDENCE a hand-written ticket
// would carry — counts, spread, window, the normalized shape — because
// FNP-903's open question 2 flags that an auto-filed ticket carrying only an
// error string is worse than the hand-written ones it replaces.

const formatWhen = (value?: string | null): string => {
    if (!value) {
        return 'unknown';
    }
    // Rendered as the raw ISO string on purpose: this text is pasted into a
    // ticket that other people read in other timezones, so a localized string
    // would be actively misleading there.
    return value;
};

/**
 * A one-line summary suitable for the Jira issue title.
 *
 * Uses the NORMALIZED error rather than the sample: the placeholders
 * (<uuid>, <epoch>) are what make two occurrences the same defect, so a title
 * built from them describes the DEFECT rather than one instance of it.
 */
export const buildTicketSummary = (record: DefectSignature): string => {
    const detail = (record.normalizedError ?? record.sampleError ?? '').trim();
    if (!detail) {
        return `${record.actionCode}: unexplained failure`;
    }
    // Jira summaries are single-line; keep it short enough to read in a list.
    const flattened = detail.replace(/\s+/g, ' ');
    const clipped = flattened.length > 140 ? `${flattened.slice(0, 137)}...` : flattened;
    return `${record.actionCode}: ${clipped}`;
};

export const buildTicketBody = (record: DefectSignature): string => {
    const kind = defectKindDisplay(classifyDefect(record));

    const lines = [
        'Found by the synthetic-user fleet (FNP-847) and grouped by the defect-signature engine (FNP-903).',
        '',
        `Signal type: ${kind.label} — ${kind.description}`,
        '',
        '## Evidence',
        '',
        `- Action: ${record.actionCode}`,
        `- Occurrences: ${record.occurrenceCount}`,
        `- Distinct synthetic users affected: ${record.distinctUserCount}`,
        `- First seen: ${formatWhen(record.firstSeenAt)}`,
        `- Last seen: ${formatWhen(record.lastSeenAt)}`,
        `- Outcome: ${record.outcome}${record.verificationResult ? ` / ${record.verificationResult}` : ''}`,
        `- Fingerprint: ${record.id}`,
        '',
        '## Normalized error (what makes these one defect)',
        '',
        '```',
        record.normalizedError ?? '(none recorded)',
        '```',
        '',
        '## Sample error (verbatim, one real occurrence)',
        '',
        '```',
        record.sampleError ?? '(none recorded)',
        '```',
        '',
        '## Reproduction',
        '',
        `Runs on every wake of a synthetic user whose persona includes \`${record.actionCode}\`.`,
        'The contributing observations are listed on the defect detail page in the console.',
    ];

    return lines.join('\n');
};
